import { useMemo, useRef, useState } from "react"

import { ArrowUp, Paperclip, Square, X } from "lucide-react"

import { AttachmentChip, type UploadState } from "@/components/chat/AttachmentChip"
import { EffortSlider } from "@/components/chat/EffortSlider"
import { MeetingCalendar } from "@/components/chat/MeetingCalendar"
import { ModelPicker } from "@/components/chat/ModelPicker"
import { RetrievalSwitch } from "@/components/chat/RetrievalSwitch"
import { ScopePicker } from "@/components/chat/ScopePicker"
import { SlashMenu, buildSlashCommands, type SlashCommand } from "@/components/chat/SlashMenu"
import { Button } from "@/components/ui/button"
import { ApiError, WORKIQ_AGENT_ID, uploadFile } from "@/lib/api"
import { cn } from "@/lib/utils"
import type { Agent, FileAttachment, MeetingContext, Model, RetrievalEngine, SearchScope } from "@/types/api"

const ACCEPTED_FILES = ".pdf,.docx,.xlsx,.pptx,.txt,.png,.jpg,.jpeg,.gif,.webp"

export interface ComposerHandle {
  send: (text: string) => void
}

interface ComposerProps {
  agents: Agent[]
  selectedAgent: Agent | null
  onSelectAgent: (agent: Agent | null) => void
  models: Model[]
  selectedModelId: string | null
  onSelectModel: (id: string) => void
  effortLevels: string[]
  effort: string | null
  onSelectEffort: (level: string) => void
  retrievalEngines: RetrievalEngine[]
  selectedEngineId: string | null
  onSelectEngine: (id: string) => void
  scopes: SearchScope[]
  onToggleScope: (scope: SearchScope) => void
  isStreaming: boolean
  onSend: (text: string, options: { attachment: FileAttachment | null }) => void
  onStop: () => void
  /** Work IQ mode: a meeting was picked in the calendar; `text` is the message shown in the chat. */
  onPickMeeting?: (meeting: MeetingContext, text: string) => void
}

export function Composer({
  agents,
  selectedAgent,
  onSelectAgent,
  models,
  selectedModelId,
  onSelectModel,
  effortLevels,
  effort,
  onSelectEffort,
  retrievalEngines,
  selectedEngineId,
  onSelectEngine,
  scopes,
  onToggleScope,
  isStreaming,
  onSend,
  onStop,
  onPickMeeting,
}: ComposerProps) {
  const [value, setValue] = useState("")
  const [upload, setUpload] = useState<UploadState>({ status: "idle" })
  const [slashIndex, setSlashIndex] = useState(-1)
  const [menuClosed, setMenuClosed] = useState(false)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Classic / Work IQ: Work IQ is just the `work-iq` agent being selected (like `/work-iq`).
  // The mode is switched in the profile menu. Classic mode keeps the existing agents and
  // settings (without work-iq); Work IQ mode has no other agents.
  const workIqEnabled = selectedAgent?.id === WORKIQ_AGENT_ID

  const commands = useMemo(
    () => (workIqEnabled ? [] : buildSlashCommands(agents.filter((a) => a.id !== WORKIQ_AGENT_ID))),
    [agents, workIqEnabled],
  )

  // Slash menu derives from the current input value
  const slashStart = value.lastIndexOf("/")
  const fragment = slashStart >= 0 ? value.slice(slashStart).toLowerCase() : null
  const matches = useMemo(
    () => (fragment !== null && !menuClosed ? commands.filter((c) => c.cmd.startsWith(fragment)) : []),
    [commands, fragment, menuClosed],
  )
  const menuOpen = matches.length > 0

  const attachment = upload.status === "ready" ? upload.attachment : null
  const canSend = value.trim().length > 0 && !isStreaming && upload.status !== "uploading"

  const resizeTextarea = () => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }

  const setInput = (next: string) => {
    setValue(next)
    setMenuClosed(false)
    setSlashIndex(-1)
    requestAnimationFrame(resizeTextarea)
  }

  const selectAgent = (agent: Agent) => {
    // Re-selecting the active agent toggles it off (parity with the legacy UI)
    onSelectAgent(selectedAgent?.id === agent.id ? null : agent)
  }

  const applySlashCommand = (command: SlashCommand) => {
    selectAgent(command.agent)
    const start = value.lastIndexOf("/")
    setValue(value.slice(0, start).trimEnd())
    setMenuClosed(true)
    setSlashIndex(-1)
    textareaRef.current?.focus()
  }

  const submit = (text?: string) => {
    const message = (text ?? value).trim()
    if (!message || isStreaming || upload.status === "uploading") return
    onSend(message, { attachment })
    setValue("")
    setUpload({ status: "idle" })
    setSlashIndex(-1)
    setMenuClosed(false)
    requestAnimationFrame(() => {
      resizeTextarea()
      textareaRef.current?.focus()
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (menuOpen) {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        setSlashIndex((i) => (i + 1) % matches.length)
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        setSlashIndex((i) => (i - 1 + matches.length) % matches.length)
        return
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault()
        applySlashCommand(matches[slashIndex >= 0 ? slashIndex : 0])
        return
      }
      if (event.key === "Escape") {
        setMenuClosed(true)
        setSlashIndex(-1)
        return
      }
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = "" // allow re-selecting the same file
    if (!file) return
    setUpload({ status: "uploading", filename: file.name })
    try {
      const result = await uploadFile(file)
      setUpload({ status: "ready", attachment: result })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : `Upload failed (${file.name})`
      setUpload({ status: "error", message })
    }
  }

  return (
    <div className="border-t border-border-subtle bg-background px-4 pt-3.5 pb-5">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-2">
        <AttachmentChip state={upload} onRemove={() => setUpload({ status: "idle" })} />

        {/* Controls self-hide when they don't apply; `empty:hidden` keeps the row
            from leaving a gap behind when none renders. */}
        <div className="flex flex-wrap items-center gap-2 empty:hidden">
          {!workIqEnabled && (
            <>
              <RetrievalSwitch engines={retrievalEngines} selectedId={selectedEngineId} onSelect={onSelectEngine} />
              <ScopePicker scopes={scopes} onToggle={onToggleScope} />
            </>
          )}
          <EffortSlider levels={effortLevels} value={effort} onChange={onSelectEffort} />
        </div>

        {selectedAgent && !workIqEnabled && (
          <span className="inline-flex max-w-fit items-center gap-1.5 self-start rounded-full border border-orange/35 bg-orange-tint py-1 pr-2 pl-3 text-xs text-fg1">
            <span className="truncate">{selectedAgent.description || selectedAgent.label}</span>
            <button
              type="button"
              onClick={() => onSelectAgent(null)}
              className="text-fg3 hover:text-foreground"
              aria-label="Clear agent"
            >
              <X className="size-3" />
            </button>
          </span>
        )}

        <div className="relative">
          {menuOpen && (
            <SlashMenu commands={matches} activeIndex={slashIndex >= 0 ? slashIndex : 0} onSelect={applySlashCommand} />
          )}

          <div className="flex items-end gap-2 rounded-[14px] border border-border bg-card px-2 py-1.5 shadow-card transition-colors focus-within:border-orange">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0 self-end rounded-lg text-fg3 hover:text-foreground"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Upload file"
              title="Upload file"
            >
              <Paperclip className="size-4" />
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILES}
              className="hidden"
              onChange={handleFileSelect}
            />
            {workIqEnabled && onPickMeeting && <MeetingCalendar disabled={isStreaming} onPick={onPickMeeting} />}

            <ModelPicker models={models} selectedId={selectedModelId} onSelect={onSelectModel} />

            <textarea
              ref={textareaRef}
              rows={1}
              value={value}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                workIqEnabled
                  ? "Ask across SharePoint, mail, Teams…"
                  : selectedAgent
                    ? `Asking ${selectedAgent.label}…`
                    : "Ask about your SharePoint documents…"
              }
              className="max-h-50 flex-1 resize-none self-center bg-transparent py-1.5 text-[15px] outline-none placeholder:text-fg3"
            />

            {isStreaming ? (
              <Button
                type="button"
                size="icon"
                onClick={onStop}
                aria-label="Stop generating"
                title="Stop generating"
                className="size-[38px] shrink-0 self-end rounded-lg bg-destructive text-white hover:bg-destructive/90"
              >
                <Square className="size-3.5 fill-current" />
              </Button>
            ) : (
              <Button
                type="button"
                size="icon"
                onClick={() => submit()}
                disabled={!canSend}
                aria-label="Send message"
                title="Send"
                className={cn(
                  "size-[38px] shrink-0 self-end rounded-lg transition-transform",
                  canSend && "hover:-translate-y-px hover:bg-orange-hover",
                )}
              >
                <ArrowUp className="size-[18px]" strokeWidth={2.2} />
              </Button>
            )}
          </div>
        </div>

        <p className="px-2 text-center text-[11.5px] text-fg4">
          celver Copilot can make mistakes — verify important information.
        </p>
      </div>
    </div>
  )
}
